// Libraries
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Box from "@mui/material/Box";

// Utilities
import {
  getLocalDate,
  calculateYearDifference,
} from "../../../../utils/dateHelper";
import { getDecimalNum } from "../../../../utils/numberHelper";
import useClinic from "../../../../hooks/useClinic";

const PatientInfoSection = ({ patient }) => {
  // internationalization
  const { t } = useTranslation();
  const { clinicTimezone } = useClinic();

  const sexTitles = {
    Male: t("gender_male"),
    Female: t("gender_female"),
    Undefined: t("word_na"),
  };

  const patientAge = useMemo(
    () => patient?.age ?? calculateYearDifference(patient?.dateOfBirth),
    [patient?.age, patient?.dateOfBirth]
  );

  const patientBirthday = useMemo(
    () =>
      patient?.dateOfBirth
        ? getLocalDate(patient?.dateOfBirth, clinicTimezone)
        : "N/A",
    [patient?.dateOfBirth, clinicTimezone]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{ fontSize: { xs: 12, sm: 14 } }}
    >
      <Box display="flex" flexWrap="wrap" columnGap="8px">
        <span>
          <span style={{ fontWeight: "bold" }}>{t("exams_patient_age")}: </span>
          <span>{patientAge}</span>
        </span>
        <span>
          <span style={{ fontWeight: "bold" }}>
            {t("patients_modal_sex")}:{" "}
          </span>
          <span>{sexTitles[patient.sex]}</span>
        </span>
        <span>
          <span style={{ fontWeight: "bold" }}>
            {t("patients_modal_birth")}:{" "}
          </span>
          <span>{patientBirthday}</span>
        </span>
      </Box>
      <span>
        <span style={{ fontWeight: "bold" }}>{t("requires_captions")}: </span>
        <span>{patient?.requiresCaptions ? t("word_yes") : t("word_no")}</span>
      </span>
      <Box display="flex" flexWrap="wrap" columnGap="8px">
        {(patient?.rightSphere !== null ||
          patient?.rightCylinder !== null ||
          patient?.rightAxis !== null) && (
          <span>
            <span style={{ fontWeight: "bold" }}>{t("OD Rx")}: </span>
            <span>
              {patient?.rightSphere !== null
                ? getDecimalNum(patient?.rightSphere, 2)
                : "N/A"}
              {" sph | "}
              {patient?.rightCylinder !== null
                ? getDecimalNum(patient?.rightCylinder, 2)
                : "N/A"}
              {" cyl | "}
              {patient?.rightAxis !== null
                ? `${getDecimalNum(patient?.rightAxis, 0)}°`
                : "N/A"}
              {` ${t("word_axis")}`}
            </span>
          </span>
        )}
        {(patient?.leftSphere !== null ||
          patient?.leftCylinder !== null ||
          patient?.leftAxis !== null) && (
          <span>
            <span style={{ fontWeight: "bold" }}>{t("OS Rx")}: </span>
            <span>
              {patient?.leftSphere !== null
                ? getDecimalNum(patient?.leftSphere, 2)
                : "N/A"}
              {" sph | "}
              {patient?.leftCylinder !== null
                ? getDecimalNum(patient?.leftCylinder, 2)
                : "N/A"}
              {" cyl | "}
              {patient?.leftAxis !== null
                ? `${getDecimalNum(patient?.leftAxis, 0)}°`
                : "N/A"}
              {` ${t("word_axis")}`}
            </span>
          </span>
        )}
      </Box>
    </Box>
  );
};

export default PatientInfoSection;
